import { useEffect, useRef, useState } from "react";
import { Loader2, X, RefreshCcw, AlertCircle, CheckCircle2 } from "lucide-react";
import { getArcQuote, postRedemption } from "@/lib/api";

const STALE_AFTER_SEC = 60;

function ageSeconds(quotedAtIso, now) {
  if (!quotedAtIso) return null;
  try {
    const t = new Date(quotedAtIso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.max(0, Math.floor((now - t) / 1000));
  } catch {
    return null;
  }
}

/**
 * Maps any error from `POST /api/redemptions` to {title, detail, kind}.
 * Per `/app/memory/AP4D_API_CONTRACT.md` route #6.
 * Never echoes raw amounts/metadata into copy — those come from the
 * quote object the user already saw.
 */
function mapSubmitError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail || {};
  const code = detail.error;
  const reason = detail.reason || detail.message;
  if (status === 401) {
    return { kind: "auth", title: "Session expired", detail: "Please link Assos365 again to continue." };
  }
  if (status === 503 && code === "ecosystem_rate_unavailable") {
    return { kind: "no_rate", title: "Ecosystem rate unavailable", detail: "Please try again in a moment." };
  }
  if (status === 503 && code === "partner_unavailable") {
    return { kind: "partner_unavailable", title: "Assos365 temporarily unavailable", detail: "Your request was not submitted. Please try again in a few minutes." };
  }
  if (status === 502 && code === "partner_protocol_violation") {
    return { kind: "protocol", title: "Could not complete safely", detail: "Assos365 returned an unexpected response. Your account was not credited and your ARC was not moved." };
  }
  if (status === 409 && code === "limit_exceeded") {
    return { kind: "limit", title: "Limit reached", detail: reason || "Amount is outside the current allowed range." };
  }
  if (status === 409 && code === "rejected") {
    if (typeof reason === "string" && reason.toLowerCase().includes("kyc")) {
      return { kind: "kyc", title: "KYC required", detail: "Complete KYC on Assos365 to redeem." };
    }
    if (typeof reason === "string" && reason.toLowerCase().includes("block")) {
      return { kind: "blocked", title: "Account not eligible", detail: "Contact Assos365 support." };
    }
    if (typeof reason === "string" && reason.toLowerCase().includes("inactive")) {
      return { kind: "inactive", title: "Account inactive", detail: "Reactivate your Assos365 account to redeem." };
    }
    return { kind: "rejected", title: "Not eligible right now", detail: "Assos365 declined this redemption. Please try again later or contact support." };
  }
  if (status === 409 && code === "user_not_linked") {
    return { kind: "auth", title: "Not linked", detail: "Re-link your Assos365 account to continue." };
  }
  if (status === 400 && code === "invalid_input") {
    return { kind: "invalid", title: "Invalid amount", detail: "Please correct the amount and try again." };
  }
  return { kind: "network", title: "Could not reach AssosPay", detail: "Please try again in a moment." };
}

/**
 * AP-4F confirmation modal.
 *
 * Lifecycle:
 *   1. Mount → fetch a fresh quote for `amount`. While loading, show spinner.
 *   2. Once loaded, show snapshot + "Confirm" button. A 1-second tick
 *      updates the quoted-age display.
 *   3. After STALE_AFTER_SEC (60 s) the quote is "stale": confirm is
 *      disabled, "Refresh quote" appears.
 *   4. Confirm fires `POST /api/redemptions` exactly once (busy guard).
 *      On 202 the parent's `onSuccess(arc_reference_id)` is invoked.
 *
 * Strict rules:
 *   * Confirm button has both `disabled` and a JS busy-flag check —
 *     double-click cannot fire two POSTs.
 *   * Failures show user-safe copy via `mapSubmitError` — raw error
 *     strings or stack traces never reach the DOM.
 */
export default function ConfirmRedemptionModal({ amount, me, onClose, onSuccess }) {
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(true);
  const [quoteError, setQuoteError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitDone, setSubmitDone] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const isMountedRef = useRef(true);
  const submitGuard = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Tick every second to update quoted-age display.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const loadQuote = async () => {
    setQuoteLoading(true);
    setQuoteError(null);
    try {
      const q = await getArcQuote(amount);
      if (!isMountedRef.current) return;
      if (q.status !== "active") {
        setQuoteError({ title: "Ecosystem rate unavailable", detail: "Please close this dialog and try again later." });
        setQuote(null);
      } else {
        setQuote(q);
      }
    } catch (e) {
      if (!isMountedRef.current) return;
      setQuoteError({ title: "Could not fetch quote", detail: "Please retry." });
    } finally {
      if (isMountedRef.current) setQuoteLoading(false);
    }
  };

  useEffect(() => {
    loadQuote();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount]);

  const age = quote ? ageSeconds(quote.quoted_at, now) : null;
  const stale = age != null && age > STALE_AFTER_SEC;

  const handleConfirm = async () => {
    if (submitGuard.current || submitting || submitDone) return;
    if (!quote || stale) return;
    submitGuard.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const out = await postRedemption({
        arcAmount: amount,
        metadata: { source: "assospay_redeem_ui", mode: "request_only" },
      });
      if (!isMountedRef.current) return;
      setSubmitDone(true);
      onSuccess?.(out.arc_reference_id);
    } catch (e) {
      if (!isMountedRef.current) return;
      setSubmitError(mapSubmitError(e));
      // Allow retry on transient errors (network/partner_unavailable/no_rate).
      submitGuard.current = false;
    } finally {
      if (isMountedRef.current) setSubmitting(false);
    }
  };

  const confirmDisabled = !quote || quoteLoading || stale || submitting || submitDone;
  const confirmCopy = submitDone
    ? "Submitted"
    : submitting
    ? "Submitting…"
    : stale
    ? "Refresh quote first"
    : "Confirm submit";

  return (
    <div
      data-testid="confirm-redemption-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={submitting ? undefined : onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-md glass rounded-md p-6 border border-white/15">
        <button
          type="button"
          onClick={submitting ? undefined : onClose}
          disabled={submitting}
          aria-label="Close"
          data-testid="confirm-modal-close"
          className="absolute right-3 top-3 p-1 text-white/50 hover:text-white disabled:opacity-30"
        >
          <X className="h-4 w-4" />
        </button>

        <h2 className="font-display text-base uppercase tracking-[0.2em] text-white">
          Confirm redemption
        </h2>

        {quoteLoading && (
          <div data-testid="confirm-modal-loading" className="mt-6 flex items-center gap-2 text-white/60">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="font-mono text-xs">fetching fresh quote…</span>
          </div>
        )}

        {quoteError && !quote && (
          <div data-testid="confirm-modal-quote-error" className="mt-6 flex items-start gap-2 text-[#FF3B30]">
            <AlertCircle className="mt-0.5 h-4 w-4" />
            <div className="font-mono text-xs">
              <div className="font-display text-sm text-white">{quoteError.title}</div>
              <div className="mt-1 text-white/60">{quoteError.detail}</div>
            </div>
          </div>
        )}

        {quote && !quoteLoading && (
          <div data-testid="confirm-modal-snapshot" className="mt-5">
            <Row label="ARC amount">
              <span data-testid="confirm-modal-arc">{quote.arc_amount}</span>{" "}
              <span className="text-[#00F0FF]">ARC</span>
            </Row>
            <Row label="EUR preview" highlight>
              <span data-testid="confirm-modal-eur">{quote.eur_amount}</span>{" "}
              <span className="text-[#00F0FF]">EUR</span>
            </Row>
            <Row label="Rate (eur / arc)" subtle>
              {quote.eur_per_arc}
            </Row>
            <Row label="Rate id" subtle>
              <span data-testid="confirm-modal-rate-id" className="font-mono text-[10px] text-white/55">
                {quote.rate_id}
              </span>
            </Row>
            <Row label="Destination" subtle>
              Assos365 ●●●●{me?.internal_user_id_last4 || "----"}
            </Row>
            <div
              data-testid="confirm-modal-quote-age"
              data-stale={stale ? "true" : "false"}
              className={`mt-3 flex items-center gap-2 rounded border px-3 py-2 font-mono text-[10px] ${stale ? "text-[#FFB800] border-[#FFB800]/40" : "text-white/55 border-white/10"}`}
            >
              {stale ? <AlertCircle className="h-3 w-3" /> : <RefreshCcw className="h-3 w-3" />}
              {age == null ? (
                "Rate locked at quote"
              ) : stale ? (
                <>Quote is stale ({age}s old) — refresh to continue.</>
              ) : (
                <>Rate locks at submit · quoted {age}s ago</>
              )}
              {stale && (
                <button
                  type="button"
                  onClick={loadQuote}
                  data-testid="confirm-modal-refresh-quote"
                  className="ml-auto rounded border border-[#FFB800]/40 px-2 py-0.5 text-[10px] text-[#FFB800] hover:bg-[#FFB800]/10"
                >
                  Refresh quote
                </button>
              )}
            </div>

            <p data-testid="confirm-modal-prelaunch" className="mt-4 font-mono text-[10px] leading-relaxed text-white/40">
              Pre-launch: redemptions may be reviewed by Assos365 before
              credit is issued. Your account will not be credited
              automatically.
            </p>
          </div>
        )}

        {submitError && (
          <div
            data-testid="confirm-modal-submit-error"
            data-error-kind={submitError.kind}
            className="mt-4 flex items-start gap-2 rounded border border-[#FF3B30]/40 bg-[#FF3B30]/5 px-3 py-2 text-[#FF3B30]"
          >
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div className="font-mono text-[11px]">
              <div className="font-display text-xs text-white">{submitError.title}</div>
              <div className="mt-0.5 text-white/65">{submitError.detail}</div>
            </div>
          </div>
        )}

        <div className="mt-5 flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="confirm-modal-cancel-btn"
            className="btn-secondary text-xs"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            data-testid="confirm-modal-submit-btn"
            data-busy={submitting ? "true" : "false"}
            data-stale={stale ? "true" : "false"}
            className="btn-primary text-xs flex-1 inline-flex items-center justify-center gap-1.5 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {submitDone && <CheckCircle2 className="h-3.5 w-3.5" />}
            {confirmCopy}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children, highlight, subtle }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</span>
      <span
        className={`font-display ${
          highlight ? "text-xl text-[#39FF14] glow-green" : subtle ? "text-xs text-white/70" : "text-base text-white"
        }`}
      >
        {children}
      </span>
    </div>
  );
}
